import FolderOutlined from "@mui/icons-material/FolderOutlined";
import PermMediaOutlined from "@mui/icons-material/PermMediaOutlined";
import { Box, TextField } from "@mui/material";
import { type TreeItemProps, TreeItem } from "@mui/x-tree-view";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSupabaseCallback } from "../../../server/supabase/hooks";
import type { UUID } from "../../../utils/uuid";
import { SupabaseFolderService } from "../server/folder-service";
import { ROOT_ID } from "../utils";
import { CustomTreeItemMenu } from "./custom-tree-item-menu";

type CustomTreeItemProps = TreeItemProps & {
  reloadFolders?: () => void;
  onCreateSubFolder?: (parentFolderId: UUID | null) => void;
};

export const CustomTreeItem = ({
  itemId,
  label,
  slotProps,
  reloadFolders,
  onCreateSubFolder,
  ...rest
}: CustomTreeItemProps): React.ReactElement => {
  const { t } = useTranslation();
  const [local, setLocal] = useState<string>(label as string);
  const lastCommittedNameRef = useRef<string>(label as string);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const isRoot = itemId === ROOT_ID;
  const menuOpen = Boolean(menuAnchor);

  const saveRename = useSupabaseCallback(
    async ({ supabase }, name: string) => {
      const service = new SupabaseFolderService(supabase);

      const { error } = await service.update(itemId as UUID, { name });

      if (error) {
        if (error.message.includes("folder_unique_name")) {
          toast.error(t("Folder name must be unique"));
        } else {
          toast.error(t("Error when renaming folder"));
        }
        throw error;
      } else {
        toast.success(t("Folder renamed"));
      }
    },
    [itemId, t],
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleRenameStart = (): void => {
    setMenuAnchor(null);
    lastCommittedNameRef.current = local;
    setIsRenaming(true);
  };

  const commitRename = (name: string): void => {
    const cleaned = name.trim();
    if (cleaned === "" || cleaned === lastCommittedNameRef.current) {
      setIsRenaming(false);
      return;
    }

    setLocal(cleaned);
    void persistFolderName(cleaned, lastCommittedNameRef.current);
  };

  const persistFolderName = async (newValue: string, previousValue: string): Promise<void> => {
    try {
      await saveRename(newValue);
      lastCommittedNameRef.current = newValue;
      setIsRenaming(false);
      reloadFolders?.();
    } catch (e) {
      setLocal(previousValue);
      setIsRenaming(false);
      console.error(e);
    }
  };

  useEffect(() => {
    if (isRenaming) {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }
  }, [isRenaming]);

  return (
    <TreeItem
      {...rest}
      itemId={itemId}
      slotProps={{
        ...slotProps,
        label: {
          ...slotProps?.label,
          style: { display: "flex", alignItems: "center", gap: 8, width: "100%" },
        },
      }}
      label={
        <Box
          data-menu-open={menuOpen}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            "& .folder-menu-button": {
              opacity: 0,
              pointerEvents: "none",
              transition: "opacity 120ms ease-in-out",
            },
            "&:hover .folder-menu-button, &:focus-within .folder-menu-button, &[data-menu-open='true'] .folder-menu-button":
              {
                opacity: 1,
                pointerEvents: "auto",
              },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            {isRoot ? <PermMediaOutlined fontSize="small" /> : <FolderOutlined fontSize="small" />}
            {isRenaming ? (
              <TextField
                defaultValue={local}
                inputRef={renameInputRef}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                  event.stopPropagation();

                  if (event.key === "Enter") {
                    event.preventDefault();
                    if (event.target instanceof HTMLInputElement) {
                      void commitRename(event.target.value);
                    }
                  }

                  if (event.key === "Escape") {
                    setIsRenaming(false);
                  }
                }}
                onBlur={(event) => {
                  event.stopPropagation();
                  void commitRename(event.target.value);
                }}
                aria-label={t("Rename folder")}
                size="small"
                variant="standard"
                sx={{ flexGrow: 1, minWidth: 0 }}
              />
            ) : (
              local
            )}
          </Box>
          {!isRoot && (
            <CustomTreeItemMenu
              menuOpen={menuOpen}
              menuAnchor={menuAnchor}
              onMenuOpen={handleMenuOpen}
              onMenuClose={() => setMenuAnchor(null)}
              onRename={handleRenameStart}
              onCreateSubFolder={() => onCreateSubFolder?.(isRoot ? null : (itemId as UUID))}
            />
          )}
        </Box>
      }
    />
  );
};
